import { useState, useRef, useEffect } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { type Conversation, type Activity } from '@/data/mockOrdersData'
import { mockContacts } from '@/data/mockOrdersData'
import { ChannelSelector } from './ChannelSelector'
import { PreferredContactSelector } from './PreferredContactSelector'
import { format } from 'date-fns'
import {
	Send,
	MessageCircle,
	MoreVertical,
	ArrowLeft,
	Copy
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ConversationPanelProps {
	conversation?: Conversation
	onBack?: () => void
	mode?: 'fullscreen' | 'halfscreen'
	className?: string
	shouldAutoFocus?: boolean
}

const MessageBubble = ({ activity, isOwn }: { activity: Activity; isOwn: boolean }) => {
	const isMessage = activity.type === 'message'
	const content = activity.message_content || activity.summary || activity.transcript
	
	if (!isMessage && !content) {
		// System message (stage changes, etc.)
		return (
			<div className="flex justify-center my-4">
				<div className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
					{activity.summary || `${activity.type} activity`}
				</div>
			</div>
		)
	}

	return (
		<div className={cn(
			"flex gap-2 mb-4",
			isOwn ? "justify-end" : "justify-start"
		)}>
			{!isOwn && (
				<Avatar className="h-8 w-8 flex-shrink-0">
					<AvatarImage src="" />
					<AvatarFallback className="text-xs">
						{mockContacts.find(c => c.id === activity.contact_id)?.display_name
							.split(' ')
							.map((n) => n[0])
							.join('')
							.toUpperCase()}
					</AvatarFallback>
				</Avatar>
			)}
			
			<div className={cn(
				"max-w-[70%] space-y-1",
				isOwn && "items-end"
			)}>
				<div className={cn(
					"rounded-2xl px-4 py-2 break-words",
					isOwn 
						? "bg-primary text-primary-foreground" 
						: "bg-muted/80 text-foreground"
				)}>
					{isMessage ? (
						<p className="text-sm whitespace-pre-wrap">{content}</p>
					) : (
						<div className="text-sm">
							<div className="font-medium mb-1 capitalize">{activity.type}</div>
							<p className="opacity-90">{content}</p>
							{activity.call_recording_url && (
								<Button variant="link" size="sm" className="p-0 h-auto text-xs mt-1">
									Listen to recording
								</Button>
							)}
						</div>
					)}
				</div>
				
				<div className={cn(
					"flex items-center gap-2 text-xs text-muted-foreground",
					isOwn && "justify-end"
				)}>
					<span>{format(new Date(activity.created_at), 'h:mm a')}</span>
				</div>
			</div>
			
			{isOwn && (
				<Avatar className="h-8 w-8 flex-shrink-0">
					<AvatarImage src="" />
					<AvatarFallback className="text-xs bg-primary text-primary-foreground">
						YOU
					</AvatarFallback>
				</Avatar>
			)}
		</div>
	)
}

export function ConversationPanel({ 
	conversation, 
	onBack, 
	mode = 'fullscreen',
	className,
	shouldAutoFocus = false
}: ConversationPanelProps) {
	const [messageText, setMessageText] = useState('')
	const [hoveredContactInfo, setHoveredContactInfo] = useState<string | null>(null)
	const [currentChannel, setCurrentChannel] = useState<string>('') // State for selected sending channel
	const [isInputFocused, setIsInputFocused] = useState(false) // Track input focus state
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const messageInputRef = useRef<HTMLInputElement>(null)
	
	const contact = conversation ? mockContacts.find(c => c.id === conversation.contact_id) : null

	// Set default channel to contact's preferred method when conversation changes
	useEffect(() => {
		if (contact?.preferred_contact_method) {
			// If preferred method is 'phone', default to 'sms' for messaging
			const channelForMessaging = contact.preferred_contact_method === 'phone' ? 'sms' : contact.preferred_contact_method
			setCurrentChannel(channelForMessaging)
		}
	}, [contact?.preferred_contact_method, conversation?.id])

	// Auto-scroll to bottom when messages change
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}, [conversation?.messages])

	// Auto-focus message input when opening for a specific order
	useEffect(() => {
		if (shouldAutoFocus && conversation && messageInputRef.current) {
			// Small delay to ensure the modal is fully rendered
			const timer = setTimeout(() => {
				messageInputRef.current?.focus()
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [shouldAutoFocus, conversation?.id])

	if (!conversation || !contact) {
		return (
			<div className={cn("flex flex-col h-full", className)}>
				<div className="flex items-center justify-center flex-1 text-center text-muted-foreground">
					<div className="space-y-2">
						<MessageCircle className="h-12 w-12 mx-auto opacity-50" />
						<p className="text-sm font-medium">Select a conversation</p>
						<p className="text-xs">Choose a conversation from the list to view messages</p>
					</div>
				</div>
			</div>
		)
	}

	// Show all activities without filtering
	const filteredActivities = conversation.messages

	const handleSendMessage = () => {
		if (!messageText.trim()) return
		
		// TODO: Implement actual message sending
		setMessageText('')
	}

	return (
		<div className={cn("flex flex-col h-full", className)}>
			{/* Header */}
			<div className="flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur">
				<div className="flex items-center gap-3">
					{mode === 'halfscreen' && onBack && (
						<Button variant="ghost" size="sm" onClick={onBack}>
							<ArrowLeft className="h-4 w-4" />
						</Button>
					)}
					
					<Avatar className="h-10 w-10">
						<AvatarImage src="" alt={contact.display_name} />
						<AvatarFallback>
							{contact.display_name
								.split(' ')
								.map((n) => n[0])
								.join('')
								.toUpperCase()}
						</AvatarFallback>
					</Avatar>
					
					<div className="flex-1">
						<div className="flex items-center gap-2">
							<div className="font-semibold">{contact.display_name}</div>
							<PreferredContactSelector
								contact={contact}
								onPreferredMethodChange={(_method) => {
									// TODO: Update contact's preferred method
								}}
							/>
						</div>
						<div className="flex items-center gap-4 text-sm text-muted-foreground">
							{/* Contact Info with Copy Buttons */}
							{contact.phone_raw && (
								<div 
									className="flex items-center gap-1 group"
									onMouseEnter={() => setHoveredContactInfo('phone')}
									onMouseLeave={() => setHoveredContactInfo(null)}
								>
									<span>{contact.phone_raw}</span>
									{hoveredContactInfo === 'phone' && (
										<Button
											variant="ghost"
											size="sm"
											className="h-6 w-6 p-0 opacity-70 hover:opacity-100"
											onClick={() => navigator.clipboard.writeText(contact.phone_raw || '')}
										>
											<Copy className="h-3 w-3" />
										</Button>
									)}
								</div>
							)}
							
							{contact.email && (
								<div 
									className="flex items-center gap-1 group"
									onMouseEnter={() => setHoveredContactInfo('email')}
									onMouseLeave={() => setHoveredContactInfo(null)}
								>
									<span>{contact.email}</span>
									{hoveredContactInfo === 'email' && (
										<Button
											variant="ghost"
											size="sm"
											className="h-6 w-6 p-0 opacity-70 hover:opacity-100"
											onClick={() => navigator.clipboard.writeText(contact.email || '')}
										>
											<Copy className="h-3 w-3" />
										</Button>
									)}
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="flex items-center gap-2">
					<Button variant="ghost" className="h-9 w-9">
						<MoreVertical className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{/* Messages */}
			<div className="flex-1 overflow-auto p-4 space-y-1">
				{filteredActivities.map((activity) => (
					<MessageBubble 
						key={activity.id} 
						activity={activity} 
						isOwn={activity.direction === 'outbound'} 
					/>
				))}
				<div ref={messagesEndRef} />
			</div>

			{/* Message Input */}
			<div className="px-4 pt-4 pb-0 border-t bg-background/95 backdrop-blur">
				<div className="space-y-2">
					<div className="flex items-center gap-3">
					<Input
						ref={messageInputRef}
						placeholder={`Message ${contact.display_name}...`}
						value={messageText}
						onChange={(e) => setMessageText(e.target.value)}
						onFocus={() => setIsInputFocused(true)}
						onBlur={() => setIsInputFocused(false)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault()
								handleSendMessage()
							}
						}}
						className="flex-1 h-9"
					/>
						<ChannelSelector
							contact={contact}
							currentChannel={currentChannel}
							onChannelChange={setCurrentChannel}
						/>
						<Button 
							onClick={handleSendMessage}
							disabled={!messageText.trim()}
							className="h-9 w-9"
						>
							<Send className="h-4 w-4" />
						</Button>
					</div>
					<div className="mt-[2px] h-[18px] overflow-hidden">
						<p
							className={cn(
								"text-[0.88rem] italic leading-[1.05rem] text-[#3A2E2F]/70 transition-opacity duration-200 truncate",
								isInputFocused ? "opacity-100" : "opacity-0"
							)}
						>
							Press Enter to send • Shift+Enter for new line
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}
